import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { AuthService } from '@auth0/auth0-angular';
import { ButtonModule } from 'primeng/button';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { AuthHeaderComponent } from '../../../shared/ui/auth-layout/auth-header.component';
import { AuthFooterComponent } from '../../../shared/ui/auth-layout/auth-footer.component';
import { OnboardingService } from '../../../core/services/onboarding.service';
import { UserStateService } from '../../../core/services/user-state.service';
import { LoggingService } from '../../../core/services/logging.service';
import { interval, Subscription } from 'rxjs';
import { switchMap } from 'rxjs/operators';

@Component({
  selector: 'app-email-verification',
  standalone: true,
  imports: [CommonModule, RouterModule, ButtonModule, ToastModule, AuthHeaderComponent, AuthFooterComponent],
  providers: [MessageService],
  templateUrl: './email-verification.component.html',
  styleUrls: ['./email-verification.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EmailVerificationComponent implements OnInit, OnDestroy {
  userEmail: string = '';
  isResending: boolean = false;
  private pollingSubscription?: Subscription;

  constructor(
    private auth: AuthService,
    private router: Router,
    private onboardingService: OnboardingService,
    private messageService: MessageService,
    private userStateService: UserStateService,
    private logger: LoggingService
  ) {}

  ngOnInit() {
    // Get user email from Auth0
    this.auth.user$.subscribe(user => {
      if (user?.email) {
        this.userEmail = user.email;
      }
    });

    // Start polling for email verification status every 3 seconds
    this.startPolling();
  }

  ngOnDestroy() {
    // Clean up polling subscription
    if (this.pollingSubscription) {
      this.pollingSubscription.unsubscribe();
    }
  }

  private startPolling() {
    // Poll every 3 seconds
    this.pollingSubscription = interval(3000)
      .pipe(
        switchMap(() => this.onboardingService.getOnboardingStatus())
      )
      .subscribe({
        next: (status) => {
          if (status.emailVerified) {
            // Email is verified, update state immediately
            this.logger.info('Email verified, updating state and redirecting...');
            this.userStateService.updateState({ emailVerified: true });

            // Stop polling
            if (this.pollingSubscription) {
              this.pollingSubscription.unsubscribe();
            }

            // Redirect to resolving
            this.router.navigate(['/resolving']);
          }
        },
        error: (error) => {
          this.logger.error('Error checking verification status:', error);
          // Continue polling even on error
        }
      });
  }

  resendVerificationEmail() {
    if (this.isResending) {
      return;
    }

    this.isResending = true;

    this.onboardingService.resendVerificationEmail().subscribe({
      next: (response) => {
        this.messageService.add({
          severity: 'success',
          summary: 'Email Sent',
          detail: response.message || 'Verification email sent successfully. Please check your inbox.',
          life: 5000
        });
        this.isResending = false;
      },
      error: (error) => {
        this.logger.error('Error resending verification email:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to send verification email. Please try again later.',
          life: 5000
        });
        this.isResending = false;
      }
    });
  }

  goToLogin() {
    this.router.navigate(['/']);
  }
}
